// ============================================
// themes.js
// Single source of truth for chart visual theming. Each theme is a flat
// object describing colors and a few numeric parameters.
//
// Cell gradient — TWO formats supported:
//   1. Legacy grayscale:  { loRgb: 32, hiRgb: 135 }  → 2-stop ramp
//      `rgb(32,32,32)` at zero volume → `rgb(135,135,135)` at peak.
//   2. Multi-stop color:  { stops: [[0, [r,g,b]], ..., [1, [r,g,b]]] }
//      Linear-RGB interpolation between consecutive stops. Use this
//      when you need a non-grayscale gradient (e.g. viridis-style
//      purple → blue → teal → yellow).
//
// PER-SIDE cell gradients: a theme's `cell` block describes the SHARED
// gradient used by both the buy and sell halves of a footprint cell.
// User overrides (see resolveTheme below) can split that into
// `cell.buyGrad` / `cell.sellGrad`, each in either format above. The
// canvas asks for both via buildSideLUTs() and never assumes they match.
//
// Both formats produce a 256-entry LUT (buildLUT below) so the per-cell
// render is just an indexed read. LUTs are cached by gradient signature
// at module scope, so re-resolving a theme after a color edit rebuilds
// only the ramps that actually changed.
//
// Adding a theme: append a new entry to THEMES with a unique id. No
// other code changes required. The picker auto-discovers them.
// ============================================

const baseLegacyCell = {
  textLightThreshold: 135,
  textOnDark:  '#ffffff',
  textOnLight: '#1a1a1a',
}

export const THEMES = {
  mono: {
    id:   'mono',
    name: 'Mono',
    description: 'High-contrast monochrome — analytical feel',
    cell: {
      loRgb: 18,
      hiRgb: 200,
      textLightThreshold: 150,
      textOnDark:  '#ffffff',
      textOnLight: '#0a0a0a',
    },
    imbalance: {
      buy:  '#79d19b',
      sell: '#ff9696',
    },
    volumeProfile: {
      buy: '#333333',
      sell: '#202020',
    },
    candle: {
      bullBody:        '#5f79ce',
      bearBody:        '#3f3f3f',
      bullStroke:      '#000000',
      bearStroke:      '#cfcfcf',
      wickFootprint:   '#ffffff',
      wickCandle:      '#cfcfcf',
      wickTight:       '#cfcfcf',   // zoomed-out single stick (see wickColorOf)
      historicalBullBody: 'rgb(80, 80, 80)',
      historicalBearBody: 'rgb(28, 28, 28)',
      historicalStroke:   'rgb(120, 120, 120)',
    },
  },

  default: {
    id:   'default',
    name: 'Neon',
    description: 'The original — grey gradient with neon imbalance accents',
    cell: {
      loRgb: 18,
      hiRgb: 200,
      ...baseLegacyCell,
    },
    imbalance: {
      buy:  '#00ff84',
      sell: '#ff5656',
    },
    volumeProfile: {
      buy: '#2c2f52',
      sell: '#2b2b2b',
    },
    candle: {
      bullBody:        '#00ff6a',
      bearBody:        '#ff1744',
      bullStroke:      '#009e00',
      bearStroke:      '#cc1235',
      wickFootprint:   '#ffffff',
      wickCandle:      '#dddddd',
      wickTight:       '#dddddd',   // zoomed-out single stick (see wickColorOf)
      historicalBullBody: 'rgb(58, 58, 58)',
      historicalBearBody: '#1a1a24',
      historicalStroke:   'rgb(105, 105, 105)',
    },
  },

  synthwave: {
    id:   'synthwave',
    name: 'Synthwave',
    description: 'Pink and cyan, dusk-on-the-strip vibe',
    cell: {
      loRgb: 18,
      hiRgb: 200,
      textLightThreshold: 130,
      textOnDark:  '#ffe6ff',
      textOnLight: '#1a0030',
    },
    imbalance: {
      buy:  '#00f0ff',
      sell: '#ff2dd1',
    },
    volumeProfile: {
      buy: '#372863',
      sell: '#5a264f',
    },
    candle: {
      bullBody:        '#00f0ff',
      bearBody:        '#ff2dd1',
      bullStroke:      '#00b3c0',
      bearStroke:      '#c91ea3',
      wickFootprint:   '#ffe6ff',
      wickCandle:      '#e0c0ff',
      wickTight:       '#e0c0ff',   // zoomed-out single stick (see wickColorOf)
      historicalBullBody: 'rgb(45, 45, 80)',
      historicalBearBody: 'rgb(50, 25, 60)',
      historicalStroke:   'rgb(140, 110, 180)',
    },
  },

  viridis: {
    id:   'viridis',
    name: 'Viridis',
    description: 'Heatmap gradient — purple low, yellow high, teal between',
    cell: {
      // Multi-stop color ramp. t∈[0,1] maps to (volume / candleMaxVolume).
      // Picked to roughly match the matplotlib viridis colormap so the
      // visual reads "depth-of-market heatmap" at a glance.
      stops: [
        [0.00, [ 55,  35,  95]],   // deep purple   — quietest cells
        [0.30, [ 65,  85, 165]],   // indigo blue
        [0.55, [ 70, 175, 145]],   // teal
        [0.78, [180, 220,  80]],   // yellow-green
        [1.00, [250, 230,  50]],   // bright yellow — peak volume
      ],
      // Yellow cells need dark text; bumped threshold up since the
      // gradient brightens later than a simple grayscale.
      textLightThreshold: 165,
      textOnDark:  'rgb(255, 250, 176)',
      textOnLight: '#15101e',
    },
    imbalance: {
      // Imbalance must read on yellow (peak) cells. Lime stays bright on
      // yellow because it pushes the green channel; pink-red contrasts
      // with both the yellow peaks and the purple troughs.
      buy:  '#3cff00',
      sell: '#ff2d6e',
    },
    volumeProfile: {
      buy: '#2a4d37',
      sell: '#48234d',
    },
    candle: {
      // Mirror the gradient endpoints: warm yellow-green for bull,
      // deep purple-blue for bear. Reads consistent with the cell heat.
      bullBody:        '#00ff95',
      bearBody:        '#5a4ed8',
      bullStroke:      '#8fa829',
      bearStroke:      '#3a32a0',
      wickFootprint:   '#ffffff',
      wickCandle:      '#d0d8b8',
      wickTight:       '#d0d8b8',   // zoomed-out single stick (see wickColorOf)
      historicalBullBody: 'rgb(60, 70, 50)',
      historicalBearBody: 'rgb(45, 38, 75)',
      historicalStroke:   'rgb(120, 130, 100)',
    },
  },
}

export const DEFAULT_THEME_ID = 'mono'
export const THEME_STORAGE_KEY = 'solcheddar_theme_id'
// Per-theme user color overrides: { [themeId]: { 'candle.bullBody': '#rrggbb', ... } }
// Any 'candle.*' key works here, including 'candle.wickTight' (the zoomed-out
// wick stick) — resolveTheme merges it; a picker only needs a
// THEME_COLOR_GROUPS entry { path: 'candle.wickTight', label: ... } to show it.
export const THEME_OVERRIDE_STORAGE_KEY = 'solcheddar_theme_overrides'

// ---------------------------------------------------------------------
// Color coercion. Theme values are authored as either #rgb / #rrggbb or
// `rgb(r,g,b)` strings; <input type="color"> only speaks #rrggbb, so
// every value shown in the picker goes through toHex first.
// ---------------------------------------------------------------------
const clamp255 = (n) => (n < 0 ? 0 : n > 255 ? 255 : n | 0)
const hex2 = (n) => clamp255(n).toString(16).padStart(2, '0')

export const toHex = (c) => {
  if (!c) return '#000000'
  const s = String(c).trim()
  if (s.charCodeAt(0) === 35 /* # */) {
    if (s.length === 4) return ('#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3]).toLowerCase()
    if (s.length >= 7) return s.slice(0, 7).toLowerCase()
    return '#000000'
  }
  const m = s.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i)
  if (m) return '#' + hex2(+m[1]) + hex2(+m[2]) + hex2(+m[3])
  return '#000000'
}

export const hexToRgb = (c) => {
  const s = toHex(c)
  return [
    parseInt(s.slice(1, 3), 16),
    parseInt(s.slice(3, 5), 16),
    parseInt(s.slice(5, 7), 16),
  ]
}

// ---------------------------------------------------------------------
// Gradient helpers. A "gradient" is `{ stops }` or `{ loRgb, hiRgb }`.
// ---------------------------------------------------------------------
const gradOf = (cellOrGrad) => {
  const c = cellOrGrad || {}
  if (Array.isArray(c.stops) && c.stops.length >= 2) return { stops: c.stops }
  return {
    loRgb: typeof c.loRgb === 'number' ? c.loRgb : 32,
    hiRgb: typeof c.hiRgb === 'number' ? c.hiRgb : 135,
  }
}

const gradStops = (grad) => {
  const g = gradOf(grad)
  if (g.stops) return g.stops
  return [[0, [g.loRgb, g.loRgb, g.loRgb]], [1, [g.hiRgb, g.hiRgb, g.hiRgb]]]
}

// Cache key for a gradient — two gradients with the same signature share
// one 256-entry LUT, so editing (say) the buy ramp never rebuilds sell.
const gradSig = (grad) => {
  const g = gradOf(grad)
  return g.stops
    ? 's' + g.stops.map(([t, c]) => t + ':' + c[0] + ',' + c[1] + ',' + c[2]).join('|')
    : 'g' + g.loRgb + ',' + g.hiRgb
}

const lutCache = new Map()

// Build the 256-entry color lookup table for a gradient. Includes pre-
// stringified `rgb(r,g,b)` fillStyles so the render hot path skips
// string concatenation entirely.
const buildLUT = (grad) => {
  const sig = gradSig(grad)
  const hit = lutCache.get(sig)
  if (hit) return hit

  const stops = gradStops(grad)
  const r   = new Uint8Array(256)
  const g   = new Uint8Array(256)
  const b   = new Uint8Array(256)
  const lum = new Uint8Array(256)
  const fillStyles = new Array(256)

  for (let i = 0; i < 256; i++) {
    const t = i / 255
    // Find the segment containing t (linear search; stops list is short)
    let s0 = 0
    while (s0 < stops.length - 2 && stops[s0 + 1][0] < t) s0++
    const [t0, c0] = stops[s0]
    const [t1, c1] = stops[s0 + 1]
    const k = t1 === t0 ? 0 : Math.max(0, Math.min(1, (t - t0) / (t1 - t0)))
    const rv = c0[0] + (c1[0] - c0[0]) * k
    const gv = c0[1] + (c1[1] - c0[1]) * k
    const bv = c0[2] + (c1[2] - c0[2]) * k
    r[i]   = rv | 0
    g[i]   = gv | 0
    b[i]   = bv | 0
    // Perceptual luminance — used by the text-color picker to decide
    // between textOnDark and textOnLight on a per-cell basis.
    lum[i] = (0.299 * rv + 0.587 * gv + 0.114 * bv) | 0
    fillStyles[i] = `rgb(${r[i]},${g[i]},${b[i]})`
  }

  const lut = { r, g, b, lum, fillStyles }
  lutCache.set(sig, lut)
  return lut
}

// Legacy single-ramp accessor. Kept for any caller that doesn't care
// about the buy/sell split. Result memoized on the theme object.
export const buildCellLUT = (theme) => {
  if (theme._cellLUT) return theme._cellLUT
  const lut = buildLUT(theme.cell)
  theme._cellLUT = lut
  return lut
}

// Per-side ramps. Falls back to the theme's shared cell gradient when a
// side hasn't been split, so untouched themes behave exactly as before
// (both sides get the SAME cached LUT object — zero extra memory).
export const buildSideLUTs = (theme) => {
  if (theme._sideLUTs) return theme._sideLUTs
  const shared = theme.cell
  const luts = {
    buy:  buildLUT(theme.cell?.buyGrad  || shared),
    sell: buildLUT(theme.cell?.sellGrad || shared),
  }
  theme._sideLUTs = luts
  return luts
}

// ---------------------------------------------------------------------
// Swatch endpoints for the picker.
// ---------------------------------------------------------------------
const endpointColor = (grad, which) => {
  const stops = gradStops(grad)
  const [r, g, b] = which === 'lo' ? stops[0][1] : stops[stops.length - 1][1]
  return `rgb(${r | 0},${g | 0},${b | 0})`
}

export const themeCellLowColor  = (theme) => endpointColor(theme.cell, 'lo')
export const themeCellHighColor = (theme) => endpointColor(theme.cell, 'hi')

// side: 'buy' | 'sell'; which: 'lo' | 'hi'
export const themeSideCellColor = (theme, side, which) =>
  endpointColor(theme.cell?.[side + 'Grad'] || theme.cell, which)

// CSS `linear-gradient(...)` mirroring a side's actual ramp — used for the
// live preview strips in the theme picker. Pass a RESOLVED theme so user
// edits show up; multi-stop authored ramps preview at full fidelity.
export const themeSideCssGradient = (theme, side) => {
  const stops = gradStops(theme.cell?.[side + 'Grad'] || theme.cell)
  const parts = stops.map(([t, c]) =>
    `rgb(${c[0] | 0},${c[1] | 0},${c[2] | 0}) ${(t * 100).toFixed(1)}%`)
  return `linear-gradient(90deg,${parts.join(',')})`
}

// ---------------------------------------------------------------------
// Editable color surface. Each field is addressed by a dotted path that
// doubles as the override key. The four cell.* side entries are
// SYNTHETIC — they don't exist on the authored themes; setting one (or
// both) endpoints for a side promotes that side to a 2-stop ramp.
// ---------------------------------------------------------------------
export const CELL_SIDE_PATHS = {
  'cell.buyLo':  ['buy',  'lo'],
  'cell.buyHi':  ['buy',  'hi'],
  'cell.sellLo': ['sell', 'lo'],
  'cell.sellHi': ['sell', 'hi'],
}

export const THEME_COLOR_GROUPS = [
  {
    label: 'Footprint cells',
    fields: [
      { path: 'cell.buyLo',   label: 'Buy low' },
      { path: 'cell.buyHi',   label: 'Buy high' },
      { path: 'cell.sellLo',  label: 'Sell low' },
      { path: 'cell.sellHi',  label: 'Sell high' },
    ],
  },
  {
    label: 'Cell text',
    fields: [
      { path: 'cell.textOnDark',  label: 'On dark cell' },
      { path: 'cell.textOnLight', label: 'On light cell' },
    ],
  },
  {
    label: 'Imbalance',
    fields: [
      { path: 'imbalance.buy',  label: 'Buy' },
      { path: 'imbalance.sell', label: 'Sell' },
    ],
  },
  {
    label: 'Volume profile',
    fields: [
      { path: 'volumeProfile.buy',  label: 'Buy' },
      { path: 'volumeProfile.sell', label: 'Sell' },
    ],
  },
  {
    label: 'Candles',
    fields: [
      { path: 'candle.bullBody',           label: 'Bull body' },
      { path: 'candle.bearBody',           label: 'Bear body' },
      { path: 'candle.bullStroke',         label: 'Bull stroke' },
      { path: 'candle.bearStroke',         label: 'Bear stroke' },
      { path: 'candle.wickFootprint',      label: 'Wick (footprint)' },
      { path: 'candle.wickCandle',         label: 'Wick (candle)' },
      { path: 'candle.historicalBullBody', label: 'Hist. bull' },
      { path: 'candle.historicalBearBody', label: 'Hist. bear' },
      { path: 'candle.historicalStroke',   label: 'Hist. stroke' },
    ],
  },
]

// Baseline (un-overridden) value for a field, as #rrggbb. Always pass a
// BASE theme from THEMES so the revert target can't drift.
export const themeFieldBase = (theme, path) => {
  const side = CELL_SIDE_PATHS[path]
  if (side) return toHex(endpointColor(theme.cell, side[1]))
  const dot = path.indexOf('.')
  const grp = path.slice(0, dot)
  const key = path.slice(dot + 1)
  return toHex(theme?.[grp]?.[key])
}

// Effective value for a field given the base theme + its override map.
export const themeFieldValue = (theme, overrides, path) =>
  (overrides && overrides[path]) || themeFieldBase(theme, path)

// ---------------------------------------------------------------------
// Apply a user override map onto a base theme, producing a NEW theme
// object. Returns the base object unchanged (same identity) when there's
// nothing to apply, so the common case creates no garbage and downstream
// `[theme]` memos never invalidate.
// ---------------------------------------------------------------------
export const resolveTheme = (id, overrides) => {
  const base = getTheme(id)
  const ov = overrides && overrides[id]
  if (!ov) return base
  const keys = Object.keys(ov)
  if (keys.length === 0) return base

  const cell          = { ...base.cell }
  const imbalance     = { ...base.imbalance }
  const volumeProfile = { ...(base.volumeProfile || {}) }
  const candle        = { ...base.candle }
  const buckets = { cell, imbalance, volumeProfile, candle }

  for (const k of keys) {
    const v = ov[k]
    if (!v) continue
    if (CELL_SIDE_PATHS[k]) continue        // handled below
    const dot = k.indexOf('.')
    if (dot < 1) continue
    const grp = k.slice(0, dot)
    const key = k.slice(dot + 1)
    if (buckets[grp]) buckets[grp][key] = v
  }

  // Per-side cell ramps. Only promote a side to an explicit 2-stop ramp
  // if at least one of its endpoints was actually picked — otherwise the
  // side keeps the authored gradient (so viridis stays multi-stop).
  const sideGrad = (sideName) => {
    const lo = ov[`cell.${sideName}Lo`]
    const hi = ov[`cell.${sideName}Hi`]
    if (!lo && !hi) return undefined
    return {
      stops: [
        [0, hexToRgb(lo || endpointColor(base.cell, 'lo'))],
        [1, hexToRgb(hi || endpointColor(base.cell, 'hi'))],
      ],
    }
  }
  const buyGrad  = sideGrad('buy')
  const sellGrad = sideGrad('sell')
  if (buyGrad)  cell.buyGrad  = buyGrad
  if (sellGrad) cell.sellGrad = sellGrad

  const t = { ...base, cell, imbalance, volumeProfile, candle }
  // This is a derived object — never inherit the base theme's cached
  // LUTs, or an edited gradient would keep painting the old ramp.
  delete t._cellLUT
  delete t._sideLUTs
  return t
}

export const getTheme = (id) => THEMES[id] || THEMES[DEFAULT_THEME_ID]
export const listThemes = () => Object.values(THEMES)
// ---------------------------------------------------------------------
// Custom themes (menu "+ Add"). A custom theme is a full snapshot of
// whatever theme was active (base + colour edits) when it was added.
// Stored as an array in localStorage and merged into THEMES at load, so
// getTheme / listThemes / resolveTheme treat them like built-ins.
// ---------------------------------------------------------------------
export const CUSTOM_THEME_STORAGE_KEY = 'solcheddar_custom_themes'

const readCustomThemes = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(CUSTOM_THEME_STORAGE_KEY) || '[]')
    return Array.isArray(arr) ? arr.filter(t => t && t.id && t.name && t.cell && t.candle && t.imbalance) : []
  } catch { return [] }
}
const writeCustomThemes = () => {
  try {
    const arr = Object.values(THEMES).filter(t => t.custom)
    if (arr.length) localStorage.setItem(CUSTOM_THEME_STORAGE_KEY, JSON.stringify(arr))
    else localStorage.removeItem(CUSTOM_THEME_STORAGE_KEY)
  } catch {}
}
if (typeof localStorage !== 'undefined') {
  for (const t of readCustomThemes()) if (!THEMES[t.id] || THEMES[t.id].custom) THEMES[t.id] = { ...t, custom: true }
}

export const isCustomTheme = (id) => !!(THEMES[id] && THEMES[id].custom)

// Returns the new id, or null if the name is empty / already used.
export const addCustomTheme = (name, fromTheme) => {
  const nm = String(name || '').trim().slice(0, 40)
  if (!nm || !fromTheme) return null
  if (Object.values(THEMES).some(t => t.name.toLowerCase() === nm.toLowerCase())) return null
  const snap = JSON.parse(JSON.stringify(fromTheme, (k, v) => (k === '_cellLUT' || k === '_sideLUTs') ? undefined : v))
  const id = 'custom_' + Date.now().toString(36)
  THEMES[id] = { ...snap, id, name: nm, description: 'Custom theme', custom: true }
  writeCustomThemes()
  return id
}

export const deleteCustomTheme = (id) => {
  if (!isCustomTheme(id)) return false
  delete THEMES[id]
  writeCustomThemes()
  return true
}